import React, { useState, useEffect, useRef } from 'react';
import './RecapCardFlip.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faPlus, faTimes, faBalanceScale } from '@fortawesome/free-solid-svg-icons';

/**
 * RecapCardFlip Component
 * 
 * A carousel-style component for displaying and navigating through linked recaps.
 * When no recaps are linked, shows a large "Add Recap" button.
 * When recaps are linked, transforms into a navigable card displaying all form fields.
 */
const RecapCardFlip = ({ 
  recaps = [],
  activeRecapId,
  onRecapChange,
  onUnlink,
  onAddNew,
  recapType, // 'purchase' or 'sales'
  isPending = false,
  children // Form fields to render inside the card
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const scrollContainerRef = useRef(null);
  const isInternalNavigationRef = useRef(false);

  // Scroll the active button into view
  const scrollToActiveButton = (index) => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const activeButton = container.children[index];
      
      if (activeButton) {
        const containerRect = container.getBoundingClientRect();
        const buttonRect = activeButton.getBoundingClientRect();
        
        // Calculate if button is out of view
        const isOutOfView = 
          buttonRect.left < containerRect.left || 
          buttonRect.right > containerRect.right;
        
        if (isOutOfView) {
          // Scroll button to center of container
          const scrollLeft = activeButton.offsetLeft - (container.offsetWidth / 2) + (activeButton.offsetWidth / 2);
          container.scrollTo({
            left: scrollLeft,
            behavior: 'smooth'
          });
        }
      }
    }
  };

  // Update current index when active recap changes externally (not from internal navigation)
  useEffect(() => {
    if (activeRecapId && recaps.length > 0 && !isInternalNavigationRef.current) {
      const index = recaps.findIndex(r => r.id === activeRecapId);
      if (index !== -1 && index !== currentIndex) {
        setCurrentIndex(index);
        scrollToActiveButton(index);
      }
    }
    // Reset the flag after processing
    isInternalNavigationRef.current = false;
  }, [activeRecapId, recaps]);

  // Reset to first recap when recaps array changes
  useEffect(() => {
    if (recaps.length > 0 && currentIndex >= recaps.length) {
      setCurrentIndex(0);
      scrollToActiveButton(0);
    }
  }, [recaps.length, currentIndex]);

  const handlePrevious = () => {
    if (currentIndex > 0 && !isAnimating) {
      isInternalNavigationRef.current = true;
      setIsAnimating(true);
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      scrollToActiveButton(newIndex);
      onRecapChange(recaps[newIndex].id);
      setTimeout(() => setIsAnimating(false), 300);
    }
  };

  const handleNext = () => {
    if (currentIndex < recaps.length - 1 && !isAnimating) {
      isInternalNavigationRef.current = true;
      setIsAnimating(true);
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      scrollToActiveButton(newIndex);
      onRecapChange(recaps[newIndex].id);
      setTimeout(() => setIsAnimating(false), 300);
    }
  };

  const handleDotClick = (index) => {
    if (index !== currentIndex && !isAnimating) {
      isInternalNavigationRef.current = true;
      setIsAnimating(true);
      setCurrentIndex(index);
      scrollToActiveButton(index);
      onRecapChange(recaps[index].id);
      setTimeout(() => setIsAnimating(false), 300);
    }
  };

  // If no recaps, show large "Add Recap" button
  if (!recaps || recaps.length === 0) {
    return (
      <div className="recap-card-empty">
        <button 
          type="button"
          className="recap-card-add-btn-large"
          onClick={onAddNew}
        >
          <FontAwesomeIcon icon={faPlus} style={{ marginRight: '10px', fontSize: '20px' }} />
          Add {recapType === 'purchase' ? 'Purchase' : 'Sales'} Recap
        </button>
      </div>
    );
  }

  const activeRecap = recaps[currentIndex];

  return (
    <div className="recap-card-flip">
      {/* Top Row: Label (Center) and Add Button (Right) */}
      <div className="recap-nav-top-row">
        <div className="recap-type-label">
          {recapType === 'purchase' ? 'Purchase Recaps' : 'Sales Recaps'}
        </div>
        <button 
          type="button"
          className="recap-card-add-btn-small"
          onClick={onAddNew}
        >
          <FontAwesomeIcon icon={faPlus} style={{ marginRight: '6px' }} />
          Add
        </button>
      </div>
      
      {/* Navigation Header */}
      <div className="recap-card-nav-header">
        {/* Navigation Arrows and Scrollable Recap Numbers */}
        <div className="recap-nav-bottom-row">
          <button 
            type="button"
            className="recap-nav-arrow"
            onClick={handlePrevious}
            disabled={currentIndex === 0 || isAnimating}
            aria-label="Previous recap"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          
          {/* Scrollable Recap Numbers Container */}
          <div className="recap-nav-numbers-container">
            <div className="recap-nav-numbers" ref={scrollContainerRef}>
              {recaps.map((recap, index) => (
                <button
                  key={recap.id}
                  type="button"
                  className={`recap-number-btn ${index === currentIndex ? 'active' : ''}`}
                  onClick={() => handleDotClick(index)}
                  disabled={isAnimating}
                  aria-label={`Go to recap ${recap.transaction_number}`}
                >
                  {recap.transaction_number}
                </button>
              ))}
            </div>
          </div>
          
          <button 
            type="button"
            className="recap-nav-arrow"
            onClick={handleNext}
            disabled={currentIndex === recaps.length - 1 || isAnimating}
            aria-label="Next recap"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      </div>
      
      {/* Card Container with Slide Animation */}
      <div className={`recap-card-container ${isAnimating ? 'animating' : ''}`}>
        <div className="recap-card">
          {/* Recap Header */}
          <div className="recap-card-header-bar">
            <div className="recap-card-header-left">
              <h3 className="recap-transaction-number">{activeRecap?.transaction_number}</h3>
            </div>
            <span className="recap-counter">{currentIndex + 1}/{recaps.length}</span>
            <button 
              type="button"
              className="recap-unlink-btn"
              onClick={() => onUnlink(activeRecap.id)}
              aria-label="Unlink recap"
              title="Unlink"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          
          {/* Divider Line */}
          <div className="recap-card-divider"></div>
          
          {/* Form Fields Container */}
          <div className="recap-card-fields">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecapCardFlip;
